import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PageHeader } from "@ant-design/pro-components";
import { Button, Col, ConfigProvider, Layout, Row, Select, Spin, Switch, notification } from "antd";
import { DatePicker } from "components/Utils/momentPickers";
import { ProTable } from "@ant-design/pro-components";
import _ from "lodash";
import moment from "moment";
import "moment/locale/pt-br";
import datePickerLocale from "antd/lib/locale/pt_BR";
import ptBRIntl from "antd/lib/locale/pt_BR";
import { DownloadOutlined } from "@ant-design/icons";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";
import "reactjs-popup/dist/index.css";
import { useDispatch, useSelector } from "react-redux";
import { changeSearchTerm } from "../../../auth/actions/userActions";
import { userApi } from "../../../services/actions/users";
import buildTableData from "../../../components/Utils/formData";
import buildColumns from "../../../components/Utils/formColumns";
const { RangePicker } = DatePicker;

const FormSearchResults = () => {

    const dispatch = useDispatch();
    const { search = "" } = useParams();
    const [loading, setLoading] = useState(true)
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [formsFilter, setFormsFilter] = useState([]);
    const [categoriesFilter, setCategoriesFilter] = useState([]);
    const [forms, setForms] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allData, setAllData] = useState([]);
    const [isFullSearch, setIsFullSearch] = useState(false);
    const [dates, setDates] = useState(null);
    const company = useSelector((state) => state.user.company);

    const [sorted, setSorted] = useState({});

    const fetchResultByForm = async (form = null, page = 1, sortFinal = null, isExport = false) => {

        try {
            if (sortFinal && form) {
                setSorted({
                    ...sorted,
                    [form._id]: sortFinal
                })
            }

            const sort = form ? sortFinal ? sortFinal : sorted[form._id] : null;

            const { order = null, field = null } = sort || {};

            let startDate, endDate = null;
            if (dates?.length === 2) {
                startDate = moment(dates[0]).format("YYYY-MM-DD 00:00:00");
                endDate = moment(dates[1]).format("YYYY-MM-DD 00:00:00");
            }
            if (isExport) {
                const { data, status, headers } = await userApi.downloadSearchAll(search, company.unitId, startDate, endDate, isFullSearch, page, form ? form.formName : null, order ? field : null, order);
                if (status === 200) {
                    const fileName = headers['x-filename'] || "pesquisa.xlsx";
                    await downloadFile(fileName, data)
                }
            } else {
                const {
                    data,
                    status
                } = await userApi.searchAll(search, company.unitId, startDate, endDate, isFullSearch, page, form.formName, order ? field : null, order);
                if (status === 200) {
                    if (Array.isArray(data) && data.length > 0) {
                        const first = data[0];
                        setAllData(prev => prev.map(f => f.form.schemaName === form.schemaName ? {
                            ...f,
                            items: first.items
                        } : f));
                    }
                }
            }


        } catch (ex) {
            console.log(ex)
        }
    }

    const downloadFile = async (fileName = 'pesquisa.xlsx', data) => {
        try {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(data);
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Erro ao fazer o download:', error);
        }
    };

    const searchData = useCallback(async () => {
        try {
            setLoading(true);
            let startDate, endDate = null;
            if (dates?.length === 2) {
                startDate = moment(dates[0]).format("YYYY-MM-DD 00:00:00");
                endDate = moment(dates[1]).format("YYYY-MM-DD 00:00:00");
            }
            const { data, status } = await userApi.searchAll(search, company.unitId, startDate, endDate, isFullSearch);
            if (status === 200) {
                const allForms = data.map(item => _.omit(item.form, ['fields', 'fieldsLabel', 'dateField']));
                const allCategories = _.uniqBy(data.flatMap(item => item.form.categories), '_id');
                setForms(allForms);
                setCategories(allCategories);
                setAllData(data);
                setCategoriesFilter(allCategories.map(item => item._id));
                setFormsFilter(allForms.map(item => item.schemaName));
            }
        } catch (ex) {
            console.error("Erro ao buscar dados:", ex);
        } finally {
            setLoading(false);
        }
    }, [search, company.unitId, dates, isFullSearch]);

    const handleDownloadFile = async (form = null) => {
        try {
            setLoadingDownload(true);
            await fetchResultByForm(form, 1, null, true);
        } catch (e) {
            console.log(e);
        } finally {
            setLoadingDownload(false);
        }

    }

    const renderTable = useCallback(({ form, items, pagination }) => {
        if (!formsFilter.includes(form.schemaName)) {
            return null;
        }
        const fields = isFullSearch ? form.fields : form.fieldsLabel;
        const columns = fields.map(item => item.field);
        const refFields = [
            ..._.flatMap(form.fields, "refFields"),
            ..._.flatMap(form.fields, "getValueOf")
        ].filter(Boolean);


        const columnsWithNoEllipsis = buildColumns(columns, form.fields, refFields, true).map(item => {
            return ({
                ...item,
                ellipsis: false,
                width: 200
            });
        });
        return (
            <Col span={24} key={form.schemaName}>
                <ConfigProvider locale={ptBRIntl}>
                    <ProTable
                        size="large"
                        toolbar={{
                            actions: [
                                <Button loading={loadingDownload} key="export" type={'primary'} icon={<DownloadOutlined />} onClick={() => {
                                    if (pagination.total === 0) {
                                        notification.warning({
                                            message: 'Aviso',
                                            description: 'Não há registros para serem exportados.',
                                            duration: 8,
                                        });
                                        return;
                                    }
                                    if (pagination.total > 500000) {
                                        notification.warning({
                                            message: 'Limite de exportação excedido',
                                            description: `Não é possível exportar mais de 500.000 registros simultaneamente (sua busca possui ${pagination.total.toLocaleString('pt-BR')} registros). Por favor, aplique filtros para reduzir os resultados e tente novamente.`,
                                            duration: 8,
                                        });
                                        return;
                                    }
                                    handleDownloadFile(form)
                                }}>
                                    Exportar
                                </Button>,
                            ],
                        }}

                        headerTitle={` ${form.formName} - ${pagination.total} registros encontrados`}
                        columns={columnsWithNoEllipsis}
                        columnsState={{
                            defaultValue: {
                                _id: { show: false },
                            },
                        }}
                        dataSource={buildTableData(items)}
                        rowKey="_id"
                        search={false}
                        onRow={(record, rowIndex) => {
                            return {
                                onDoubleClick: event => {
                                    window.open(`/formularios/registros/${decodeURIComponent(form.schemaName)}?id=${encodeURIComponent(record._id)}`, "_blank");
                                }
                            };
                        }}
                        onChange={async (pagination, __, sorter) => {
                            await fetchResultByForm(form, pagination.current, sorter);
                        }}
                        pagination={_.omit(pagination, ['page'])}
                        scroll={{ y: 400, x: 'max-content' }}
                    />
                </ConfigProvider>
            </Col>
        );
    }, [isFullSearch, formsFilter, allData]);


    useEffect(() => {
        dispatch(changeSearchTerm(search));
        searchData();
    }, [dispatch, searchData]);

    const handleChangeCategoriesFilter = (selectedCategories) => {
        setCategoriesFilter(selectedCategories);

        // Obtém formulários que possuem pelo menos uma das categorias selecionadas
        const filteredForms = forms.filter(form =>
            form.categories.some(cat => selectedCategories.includes(cat._id))
        ).map(form => form.schemaName);

        // Preserva os formulários sem categoria que já estavam selecionados
        const preservedForms = formsFilter.filter(formName => {
            const form = forms.find(f => f.schemaName === formName);
            return form && form.categories.length === 0;
        });

        setFormsFilter([...new Set([...filteredForms, ...preservedForms])]);
    };

    const handleChangeFormsFilter = (selectedForms) => {
        setFormsFilter(selectedForms);

        // Adiciona automaticamente as categorias dos formulários selecionados
        const updatedCategories = _.uniqBy(
            forms
                .filter(form => selectedForms.includes(form.schemaName))
                .flatMap(form => form.categories),
            "_id"
        ).map(category => category._id);

        setCategoriesFilter(updatedCategories);
    };


    const resultData = (
        <>
            <Row
                style={{
                    padding: "24px 15px 24px 15px",
                    margin: 0,
                    background: "var(--cp-surface)",
                }}
            >
                <Col xs={24} lg={12} style={{ padding: "5px" }}>
                    <span>Data </span>
                    <ConfigProvider locale={datePickerLocale}>
                        <RangePicker
                            value={dates}
                            onChange={(values) => setDates(values)}
                            ranges={{
                                Hoje: [moment(), moment()],
                                "Este mês": [
                                    moment().startOf("month"),
                                    moment().endOf("month"),
                                ],
                            }}
                            format="DD/MM/YYYY"
                            showTime
                        />
                    </ConfigProvider>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                    style={{
                        padding: "5px",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}
                >
                    <span style={{ marginRight: "5px" }}>Pesquisar em todos os campos</span>
                    <Switch checked={isFullSearch} onChange={() => setIsFullSearch(!isFullSearch)} />
                </Col>
                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                    style={{
                        padding: "5px",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}
                >
                    <Button loading={loadingDownload} key="export" type={'dashed'} icon={<DownloadOutlined />} onClick={() => {
                        const totalComb = allData.reduce((acc, curr) => acc + (curr.pagination && curr.pagination.total ? curr.pagination.total : 0), 0);
                        if (totalComb === 0) {
                            notification.warning({
                                message: 'Aviso',
                                description: 'Não há registros na pesquisa para serem exportados.',
                                duration: 8,
                            });
                            return;
                        }
                        if (totalComb > 500000) {
                            notification.warning({
                                message: 'Limite de exportação excedido',
                                description: `Não é possível exportar mais de 500.000 registros simultaneamente (sua busca possui ${totalComb.toLocaleString('pt-BR')} registros no total). Por favor, aplique filtros para reduzir os resultados.`,
                                duration: 8,
                            });
                            return;
                        }
                        handleDownloadFile() 
                    }}>
                        Exportar tudo
                    </Button>
                </Col>


                <Col xs={24} md={12} style={{ padding: "5px" }}>
                    <span>Formulários</span>
                    <Select
                        allowClear={true}
                        onChange={handleChangeFormsFilter}
                        mode="multiple"
                        style={{
                            width: "100%",
                        }}
                        options={forms?.map((form) => ({
                            value: form.schemaName,
                            label: form.formName,
                        }))}
                        value={formsFilter}
                    />
                </Col>
                <Col xs={24} md={12} style={{ padding: "5px" }}>
                    <span>Categorias</span>
                    <Select
                        allowClear={true}
                        mode={'multiple'}
                        style={{
                            width: "100%",
                        }}
                        options={categories?.map((category) => ({
                            value: category._id,
                            label: category.name,
                        }))}
                        onChange={handleChangeCategoriesFilter}
                        value={categoriesFilter}
                    />
                </Col>
            </Row>

            {loading ? (
                <Row
                    style={{
                        padding: "24px 15px 24px 15px",
                        margin: 0,
                        background: "var(--cp-surface)",
                    }}
                >
                    <Col
                        span={24}
                        style={{
                            padding: "5px",
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Spin tip="Carregando pesquisa..." size={"large"} />
                    </Col>
                </Row>
            ) : (
                <Row
                    style={{
                        padding: "24px 15px 24px 15px",
                        margin: 0,
                        background: "var(--cp-surface)",
                    }}
                >
                    {allData.map((item) => renderTable(item))}
                </Row>
            )}
        </>
    );


    return (
        <LayoutTopSide>
            <TopBar />
            <Layout style={{ overflow: "hidden" }}>
                <DrawerSider />
                <Layout
                    style={{
                        padding: "0 53px 53px",
                        height: "100%",
                        overflowY: "auto",
                        width: "100%"
                    }}
                >
                    <PageHeader
                        style={{ margin: "16px 0px 5px", paddingLeft: 0 }}
                        title={`Resultados da pesquisa${_.isEmpty(search) ? '' : `: ${search}`}`}
                        onBack={() => window.history.back()}
                    />
                    {resultData}
                </Layout>
            </Layout>
        </LayoutTopSide>
    );
}

export default FormSearchResults;
